import { useEffect, useState, useCallback } from 'react'
import {
  Shield, Users, Coins, TrendingUp, Activity,
  BarChart3, Settings, Trash2, AlertTriangle, Key, Gift, Bot, Upload, Download, Loader2, Plus, X, Save, CheckSquare, Square, Layout, Database, RefreshCw, RotateCcw, Sparkles, Zap, GripVertical, ChevronUp, ChevronDown, Eye, EyeOff, Search, Globe, CreditCard, Code, AlertCircle, CheckCircle, Clock, Star, Flame
} from 'lucide-react'
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors
} from '@dnd-kit/core'
import {
  SortableContext, sortableKeyboardCoordinates, useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import client from '../api/client'
import { useAuthStore } from '../store/authStore'
import { useTokenStore } from '../store/tokenStore'
import ApiKeys from './ApiKeys'
import LandingCms from './LandingCms'
import toast from 'react-hot-toast'


function BonusConfigForm() {
  const [registerBonus, setRegisterBonus] = useState(100)
  const [referralReferrer, setReferralReferrer] = useState(500)
  const [referralReferred, setReferralReferred] = useState(200)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    client.get('/admin/bonus-config').then(({ data }) => {
      if (data.register_bonus !== undefined) setRegisterBonus(data.register_bonus)
      if (data.referral_referrer !== undefined) setReferralReferrer(data.referral_referrer)
      if (data.referral_referred !== undefined) setReferralReferred(data.referral_referred)
    }).catch(() => {}).finally(() => setLoading(false))
  }, [])

  const handleSave = async () => {
    setSaving(true)
    setMsg(null)
    try {
      await client.put('/admin/bonus-config', {
        register_bonus: registerBonus < 0 ? 0 : registerBonus,
        referral_referrer: referralReferrer < 0 ? 0 : referralReferrer,
        referral_referred: referralReferred < 0 ? 0 : referralReferred,
      })
      setMsg('✅ Сохранено!')
    } catch {
      setMsg('❌ Ошибка сохранения')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className="text-xs text-gray-500">Загрузка...</div>

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-[10px] text-gray-600 mb-0.5">При регистрации</label>
          <input type="number" min="0" max="1000000" value={registerBonus}
            onChange={(e) => setRegisterBonus(Number(e.target.value))}
            className="input-field w-full text-sm" />
        </div>
        <div>
          <label className="block text-[10px] text-gray-600 mb-0.5">Рефереру (за приглашение)</label>
          <input type="number" min="0" value={referralReferrer}
            onChange={(e) => setReferralReferrer(Number(e.target.value))}
            className="input-field w-full text-sm" />
        </div>
        <div>
          <label className="block text-[10px] text-gray-600 mb-0.5">Приглашённому</label>
          <input type="number" min="0" value={referralReferred}
            onChange={(e) => setReferralReferred(Number(e.target.value))}
            className="input-field w-full text-sm" />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button onClick={handleSave} disabled={saving}
          className="gradient-btn px-4 py-1.5 rounded-xl text-xs disabled:opacity-40">
          {saving ? 'Сохранение...' : 'Сохранить'}
        </button>
        {msg && <span className="text-xs text-gray-400">{msg}</span>}
      </div>
    </div>
  )
}

interface AdminStats {
  totalUsers: number
  activeUsers: number
  newUsersToday: number
  newUsersWeek: number
  activeToday: number
  errorsToday: number
  topModels: [string, number][]
  purchasesToday: number
  purchasesWeek: number
  users: any[]
}

function TokensTab({ users, fetchStats }: { users: any[]; fetchStats: () => void }) {
  const fetchBalance = useTokenStore((s) => s.fetchBalance)
  const [selectedUserId, setSelectedUserId] = useState('')
  const [amount, setAmount] = useState(1000)
  const [reason, setReason] = useState('')
  const [isGranting, setIsGranting] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const me = useAuthStore((s) => s.user)

  const quickAmounts = [100, 500, 1000, 5000, 10000, 50000]

  const handleGrant = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedUserId || amount <= 0) return
    setIsGranting(true)
    setMessage(null)
    try {
      await client.post('/admin/tokens/grant', {
        user_id: selectedUserId,
        amount,
        reason: reason || 'Начислено администратором',
      })
      setMessage({ type: 'success', text: `✅ ${amount.toLocaleString()} токенов начислено!` })
      fetchStats()
      // Обновляем баланс у того, кому начислили
      const targetUser = users.find((u) => u.id === selectedUserId)
      const isCurrentUser = targetUser && useAuthStore.getState().user?.email === targetUser.email
      if (isCurrentUser) fetchBalance()
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.detail || 'Ошибка начисления' })
    } finally {
      setIsGranting(false)
    }
  }

  const handleGrantToMe = async (amount: number) => {
    if (!me) return
    setIsGranting(true)
    setMessage(null)
    try {
      await client.post('/tokens/purchase', { amount, payment_method: 'admin' })
      setMessage({ type: 'success', text: `✅ ${amount.toLocaleString()} токенов начислено!` })
      fetchStats()
      fetchBalance()
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.detail || 'Ошибка' })
    } finally {
      setIsGranting(false)
    }
  }

  return (
    <div className="space-y-4">
      {/* Быстрое пополнение себе */}
      <div className="glass-card p-6">
        <h3 className="text-sm font-semibold text-gray-200 mb-4 flex items-center gap-2">
          <Gift className="w-4 h-4 text-green-400" />
          Пополнить себе
        </h3>
        <p className="text-xs text-gray-500 mb-4">Моментальное начисление токенов на ваш аккаунт</p>
        <div className="flex flex-wrap gap-2">
          {quickAmounts.map((a) => (
            <button
              key={a}
              onClick={() => handleGrantToMe(a)}
              disabled={isGranting}
              className="px-4 py-2 rounded-xl text-sm border border-white/10 text-gray-300 hover:border-green-500/30 hover:text-green-300 hover:bg-green-500/5 transition-all disabled:opacity-40"
            >
              +{a.toLocaleString()}
            </button>
          ))}
        </div>
      </div>

      {/* Начисление любому пользователю */}
      <div className="glass-card p-6">
        <h3 className="text-sm font-semibold text-gray-200 mb-4 flex items-center gap-2">
          <Users className="w-4 h-4 text-amber-400" />
          Начислить пользователю
        </h3>

        <form onSubmit={handleGrant} className="space-y-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1.5">Пользователь</label>
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="input-field w-full text-gray-300"
            >
              <option value="" className="bg-[#1a1a2e] text-gray-400">Выберите пользователя...</option>
              {users.map((u: any) => (
                <option key={u.id} value={u.id} className="bg-[#1a1a2e] text-gray-200">
                  {u.name} ({u.email}) — {u.tokens?.toLocaleString() || 0} токенов
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1.5">Количество токенов</label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {quickAmounts.map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => setAmount(a)}
                  className={`px-2.5 py-1 rounded-lg text-xs transition-all ${amount === a ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30' : 'text-gray-500 hover:text-gray-300 border border-white/5'}`}
                >
                  {a.toLocaleString()}
                </button>
              ))}
            </div>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
              min={1}
              className="input-field w-full"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1.5">Причина (необязательно)</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Например: бонус за активность"
              className="input-field w-full"
            />
          </div>

          {message && (
            <div className={`p-3 rounded-xl text-sm ${message.type === 'success' ? 'bg-green-500/10 text-green-300 border border-green-500/20' : 'bg-red-500/10 text-red-300 border border-red-500/20'}`}>
              {message.text}
            </div>
          )}

          <button
            type="submit"
            disabled={isGranting || !selectedUserId || amount <= 0}
            className="gradient-btn px-6 py-2.5 rounded-xl text-sm flex items-center gap-2 disabled:opacity-40"
          >
            {isGranting ? 'Начисление...' : 'Начислить токены'}
          </button>
        </form>
      </div>
    </div>
  )
}


// ────────────────────────────────────────────────────────────
// AiModelsTab — вкладка управления ИИ-моделями
// ────────────────────────────────────────────────────────────

interface AiModel {
  id: number
  model_name: string
  display_name: string | null
  provider: string | null
  category: string
  is_paid: boolean
  icon_url: string | null
  system_prompt: string | null
  is_active: boolean
  sort_order: number
  cost_per_million_usd: number | null
  category_sub: string | null
  api_endpoint: string | null
}

function AiModelsTab() {
  const [models, setModels] = useState<AiModel[]>([])
  const [loading, setLoading] = useState(true)
  const [savingId, setSavingId] = useState<number | null>(null)
  const [editData, setEditData] = useState<Record<number, Partial<AiModel>>>({})
  const [showAddForm, setShowAddForm] = useState<string | null>(null)
  const [uploadingId, setUploadingId] = useState<number | null>(null)
  const [usdRate, setUsdRate] = useState<number>(80)
  const [dragId, setDragId] = useState<number | null>(null)

  // Новая модель
  const [newModel, setNewModel] = useState<Partial<AiModel>>({
    model_name: '',
    display_name: '',
    provider: '',
    category: 'chat',
    is_paid: false,
    system_prompt: '',
    icon_url: '',
    cost_per_million_usd: null,
    category_sub: null,
    api_endpoint: null,
  })
  const [creating, setCreating] = useState(false)

  const fetchModels = async () => {
    setLoading(true)
    try {
      const [modelsResp, rateResp] = await Promise.all([
        client.get('/admin/ai-models'),
        client.get('/admin/ai-models/usd-rate').catch(() => ({ data: { rate: 80 } }))
      ])
      setModels(modelsResp.data || [])
      if (rateResp.data?.rate) setUsdRate(rateResp.data.rate)
    } catch {
      setModels([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchModels()
  }, [])

  const getEditData = (m: AiModel) =>
    editData[m.id] || {}

  const updateField = (id: number, field: string, value: any) => {
    setEditData((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [field]: value },
    }))
  }

  const saveModel = async (id: number) => {
    const updates = editData[id]
    if (!updates || Object.keys(updates).length === 0) return
    setSavingId(id)
    try {
      await client.put(`/admin/ai-models/${id}`, updates)
      setEditData((prev) => {
        const next = { ...prev }
        delete next[id]
        return next
      })
      await fetchModels()
    } catch {
      // ignore
    } finally {
      setSavingId(null)
    }
  }

  const deleteModel = async (id: number) => {
    if (!window.confirm('Удалить модель?')) return
    try {
      await client.delete(`/admin/ai-models/${id}`)
      await fetchModels()
    } catch {
      // ignore
    }
  }

  const handleUploadIcon = async (id: number, file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    setUploadingId(id)
    try {
      const { data } = await client.post(`/admin/ai-models/upload-icon/${id}`, formData)
      // Обновляем editData чтобы icon_url был в данных для сохранения
      if (data?.icon_url) {
        setEditData((prev) => ({
          ...prev,
          [id]: { ...prev[id], icon_url: data.icon_url },
        }))
      }
      await fetchModels()
    } catch {
      // ignore
    } finally {
      setUploadingId(null)
    }
  }

  const createModel = async (isPaid: boolean) => {
    if (!newModel.model_name?.trim()) return
    setCreating(true)
    try {
      await client.post('/admin/ai-models', {
        model_name: newModel.model_name.trim(),
        display_name: newModel.display_name?.trim() || newModel.model_name.trim(),
        provider: newModel.provider?.trim() || null,
        category: newModel.category || 'chat',
        is_paid: isPaid,
        system_prompt: newModel.system_prompt || null,
        icon_url: newModel.icon_url || null,
        cost_per_million_usd: newModel.cost_per_million_usd ?? null,
        category_sub: newModel.category_sub ?? null,
        api_endpoint: newModel.api_endpoint ?? null,
      })
      setShowAddForm(null)
      setNewModel({ model_name: '', display_name: '', provider: '', category: 'chat', is_paid: false, system_prompt: '', icon_url: '', cost_per_million_usd: null, category_sub: null, api_endpoint: null })
      await fetchModels()
    } catch {
      // ignore
    } finally {
      setCreating(false)
    }
  }

  const chatFreeModels = models.filter((m) => m.category === 'chat' && !m.is_paid)
  const chatPaidModels = models.filter((m) => m.category === 'chat' && m.is_paid)
  const imageModels = models.filter((m) => m.category === 'image')
  const videoModels = models.filter((m) => m.category === 'video')
  const musicModels = models.filter((m) => m.category === 'music')

  const handleDragStart = (e: React.DragEvent, id: number) => {
    setDragId(id)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', String(id))
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDrop = async (e: React.DragEvent, targetId: number) => {
    e.preventDefault()
    const fromId = dragId
    if (fromId === null || fromId === targetId) return

    const current = getModelsForSubTab()
    const ids = current.models.map(m => m.id)
    const fromIdx = ids.indexOf(fromId)
    const toIdx = ids.indexOf(targetId)
    if (fromIdx === -1 || toIdx === -1) return

    const newIds = [...ids]
    newIds.splice(fromIdx, 1)
    newIds.splice(toIdx, 0, fromId)

    // Оптимистично обновляем список моделей
    const newModels = [...current.models]
    const [moved] = newModels.splice(fromIdx, 1)
    newModels.splice(toIdx, 0, moved)

    // Обновляем все модели (не только текущей вкладки — меняем sort_order глобально)
    // Сохраняем порядок для всех моделей из этой вкладки
    const allModelIds = new Set(newModels.map(m => m.id))
    const otherModels = models.filter(m => !allModelIds.has(m.id))
    const reordered = [...newModels, ...otherModels]

    setModels(reordered)
    setDragId(null)

    try {
      await client.post('/admin/ai-models/reorder', { model_ids: newIds })
    } catch {
      fetchModels() // откат при ошибке
    }
  }

  const renderModelCard = (model: AiModel) => {
    const edits = getEditData(model)
    const curDisplayName = edits.display_name ?? model.display_name ?? model.model_name
    const curProvider = edits.provider ?? model.provider
    const curSysPrompt = edits.system_prompt ?? model.system_prompt ?? ''

    return (
      <div
        key={model.id}
        draggable
        onDragStart={(e) => handleDragStart(e, model.id)}
        onDragOver={handleDragOver}
        onDrop={(e) => handleDrop(e, model.id)}
        className={`glass-card p-4 space-y-3 transition-all ${dragId === model.id ? 'opacity-40 border-indigo-500/40' : ''} ${dragId !== null && dragId !== model.id ? 'cursor-grab hover:border-indigo-500/20' : ''}`}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {/* Drag handle */}
            <div className="shrink-0 cursor-grab active:cursor-grabbing text-gray-600 hover:text-gray-400 transition-colors" title="Перетащить">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <circle cx="5" cy="3" r="1.2" /><circle cx="11" cy="3" r="1.2" />
                <circle cx="5" cy="8" r="1.2" /><circle cx="11" cy="8" r="1.2" />
                <circle cx="5" cy="13" r="1.2" /><circle cx="11" cy="13" r="1.2" />
              </svg>
            </div>
            {/* Icon */}
            <div className="relative shrink-0">
              {model.icon_url ? (
                <img src={model.icon_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500/20 to-purple-600/20 border border-white/10 flex items-center justify-center">
                  <Bot className="w-5 h-5 text-gray-500" />
                </div>
              )}
              <label className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-gray-800 border border-white/10 flex items-center justify-center cursor-pointer hover:bg-indigo-500/30 transition-colors">
                {uploadingId === model.id ? (
                  <Loader2 className="w-3 h-3 text-indigo-400 animate-spin" />
                ) : (
                  <Upload className="w-3 h-3 text-gray-400" />
                )}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/svg+xml"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) handleUploadIcon(model.id, file)
                  }}
                />
              </label>
            </div>

            <div className="min-w-0 flex-1">
              <input
                type="text"
                value={curDisplayName}
                onChange={(e) => updateField(model.id, 'display_name', e.target.value)}
                placeholder="Display name"
                className="input-field w-full text-sm font-medium text-gray-200 mb-1"
              />
              <div className="text-[10px] text-gray-600 font-mono truncate">{model.model_name}</div>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-[10px] text-gray-500">{curProvider || '—'}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-500/10 text-indigo-400">
                  {model.category}
                </span>
                {model.category === 'video' && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-400">
                    {model.category_sub === 'i2v' ? 'Фото→Видео' : model.category_sub === 'v2v' ? 'Видео→Видео' : 'Текст→Видео'}
                  </span>
                )}
                {model.category === 'image' && model.category_sub === 'edit' && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400">
                    Редактор
                  </span>
                )}
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${model.is_paid ? 'bg-amber-500/10 text-amber-400' : 'bg-emerald-500/10 text-emerald-400'}`}>
                  {model.is_paid ? 'Платная' : 'Бесплатная'}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1 shrink-0">
            {/* Toggle active */}
            <button
              onClick={async () => {
                try {
                  await client.put(`/admin/ai-models/${model.id}`, { is_active: !model.is_active })
                  await fetchModels()
                } catch {}
              }}
              className={`p-2 rounded-lg transition-all ${
                model.is_active
                  ? 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20'
                  : 'bg-gray-700/30 text-gray-500 hover:bg-gray-700/50'
              }`}
              title={model.is_active ? 'Выключить' : 'Включить'}
            >
              {model.is_active ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
            </button>
            <button
              onClick={() => saveModel(model.id)}
              disabled={savingId === model.id || !editData[model.id]}
              className="p-2 rounded-lg bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20 transition-all disabled:opacity-30"
              title="Сохранить"
            >
              {savingId === model.id ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
            </button>
            <button
              onClick={() => deleteModel(model.id)}
              className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all"
              title="Удалить модель"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Подкатегория для видео */}
        {model.category === 'video' && (
          <div>
            <label className="block text-[10px] text-gray-600 mb-1">Тип генерации</label>
            <select
              value={editData[model.id]?.category_sub ?? model.category_sub ?? 't2v'}
              onChange={(e) => updateField(model.id, 'category_sub', e.target.value || null)}
              className="input-field text-xs w-full"
            >
              <option value="">Текст → Видео (по умолчанию)</option>
              <option value="i2v">Фото → Видео</option>
              <option value="v2v">Видео → Видео</option>
            </select>
            <p className="text-[9px] text-gray-600 mt-1">В какой вкладке генератора показывать эту модель</p>
          </div>
        )}

        {/* Подкатегория для изображений */}
        {model.category === 'image' && (
          <div>
            <label className="block text-[10px] text-gray-600 mb-1">Подкатегория</label>
            <select
              value={editData[model.id]?.category_sub ?? model.category_sub ?? ''}
              onChange={(e) => updateField(model.id, 'category_sub', e.target.value || null)}
              className="input-field text-xs w-full"
            >
              <option value="">Генератор (по умолчанию)</option>
              <option value="edit">Редактор</option>
            </select>
            <p className="text-[9px] text-gray-600 mt-1">В какой вкладке показывать модель: «Генерация» или «Редактор фото»</p>
          </div>
        )}

        {/* Подкатегория для музыки */}
        {model.category === 'music' && (
          <div>
            <label className="block text-[10px] text-gray-600 mb-1">Тип трека</label>
            <select
              value={editData[model.id]?.category_sub ?? model.category_sub ?? ''}
              onChange={(e) => updateField(model.id, 'category_sub', e.target.value || null)}
              className="input-field text-xs w-full"
            >
              <option value="">Инструментальная (по умолчанию)</option>
              <option value="song">Песня (с текстом)</option>
            </select>
            <p className="text-[9px] text-gray-600 mt-1">Показывать ли поле «Текст песни» на странице генерации музыки</p>
          </div>
        )}

        {/* System prompt */}
        <div>
          <label className="block text-[10px] text-gray-600 mb-1">Системный промпт</label>
          <textarea
            value={curSysPrompt}
            onChange={(e) => updateField(model.id, 'system_prompt', e.target.value)}
            rows={2}
            placeholder="Системный промпт для модели..."
            className="input-field w-full resize-none text-xs"
          />
        </div>

        {/* Cost per million (для платных, видео и музыки) */}
        {(model.is_paid || model.category === 'video' || model.category === 'music') && (
          <div>
            <label className="block text-[10px] text-gray-600 mb-1">
              Стоимость у провайдера ($ за 1 млн токенов / за 1 шт.)
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                step="0.01"
                min="0"
                value={editData[model.id]?.cost_per_million_usd ?? model.cost_per_million_usd ?? ''}
                onChange={(e) => updateField(model.id, 'cost_per_million_usd', e.target.value ? parseFloat(e.target.value) : null)}
                placeholder="10.00"
                className="input-field w-32 text-xs"
              />
              <span className="text-[10px] text-gray-500">
                {model.category === 'image' ? 'за 1 изображение' : model.category === 'video' ? 'за 1 секунду (цена провайдера)' : model.category === 'music' ? 'за 1 трек (цена провайдера)' : 'за 1M токенов'}
              </span>
            </div>
            {model.cost_per_million_usd && model.cost_per_million_usd > 0 && (
              <div className="mt-1 text-[10px] text-indigo-400/70">
                Для пользователя:{' '}
                {model.category === 'image'
                  ? `${Math.round((editData[model.id]?.cost_per_million_usd ?? model.cost_per_million_usd ?? 0) * 2 * 160)} звёзд × 1.3 = ${Math.round((editData[model.id]?.cost_per_million_usd ?? model.cost_per_million_usd ?? 0) * 2 * 160 * 1.3)} (редактор)`
                  : model.category === 'video'
                  ? `~${Math.round((editData[model.id]?.cost_per_million_usd ?? model.cost_per_million_usd ?? 0) * 2 * 100)}⭐/сек`
                  : model.category === 'music'
                  ? `~${Math.max(1, Math.round((editData[model.id]?.cost_per_million_usd ?? model.cost_per_million_usd ?? 0) * 2 * 100))}⭐ за трек`
                  : `~${Math.round((editData[model.id]?.cost_per_million_usd ?? model.cost_per_million_usd ?? 0) / 1000000 * 2 * 133333)} токенов за 1 токен`}
              </div>
            )}
            {(model.category === 'music' || model.category === 'image' || model.category === 'video') && (
              <div>
                <label className="block text-[10px] text-gray-600 mb-1">API Endpoint</label>
                <input
                  type="text"
                  value={editData[model.id]?.api_endpoint ?? model.api_endpoint ?? ''}
                  onChange={(e) => updateField(model.id, 'api_endpoint', e.target.value || null)}
                  placeholder='lyria3/pro'
                  className="input-field w-full text-xs"
                />
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  type SubTab = 'freeChat' | 'paidChat' | 'image' | 'imageEdit' | 'videoT2v' | 'videoI2v' | 'videoV2v' | 'music'
  const [subTab, setSubTab] = useState<SubTab>('freeChat')

  const subTabs: { id: SubTab; label: string }[] = [
    { id: 'freeChat', label: 'Текстовые бесплатные' },
    { id: 'paidChat', label: 'Текстовые платные' },
    { id: 'image', label: 'Генератор картинок' },
    { id: 'imageEdit', label: 'Редактор картинок' },
    { id: 'videoT2v', label: 'Видео: Текст→Видео' },
    { id: 'videoI2v', label: 'Видео: Фото→Видео' },
    { id: 'videoV2v', label: 'Видео: Видео→Видео' },
    { id: 'music', label: 'Генератор музыки' },
  ]

  const renderAddForm = (type: 'free' | 'paid', category: string) => {
    const isPaid = type === 'paid'
    return (
      <div className="glass-card p-4 space-y-3 border border-indigo-500/30">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-semibold text-indigo-300">
            Добавить {isPaid ? 'платную' : 'бесплатную'} модель ({{
              chat: 'Чат',
              image: 'Изображения',
              video: 'Видео',
              music: 'Музыка',
            }[category] || category})
          </h4>
          <button
            onClick={() => setShowAddForm(null)}
            className="p-1 rounded-lg hover:bg-white/5 text-gray-500 hover:text-gray-200 transition-all"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] text-gray-600 mb-1">model_name *</label>
            <input
              type="text"
              value={newModel.model_name}
              onChange={(e) => setNewModel((p) => ({ ...p, model_name: e.target.value }))}
              placeholder="openai/gpt-5"
              className="input-field w-full text-xs"
            />
          </div>
          <div>
            <label className="block text-[10px] text-gray-600 mb-1">display_name</label>
            <input
              type="text"
              value={newModel.display_name ?? ''}
              onChange={(e) => setNewModel((p) => ({ ...p, display_name: e.target.value }))}
              placeholder="GPT-5"
              className="input-field w-full text-xs"
            />
          </div>
          <div>
            <label className="block text-[10px] text-gray-600 mb-1">Провайдер</label>
            <input
              type="text"
              value={newModel.provider ?? ''}
              onChange={(e) => setNewModel((p) => ({ ...p, provider: e.target.value }))}
              placeholder="openrouter, typegpt..."
              className="input-field w-full text-xs"
            />
          </div>
          <div>
            <label className="block text-[10px] text-gray-600 mb-1">Категория</label>
            <select
              value={newModel.category}
              onChange={(e) => setNewModel((p) => ({ ...p, category: e.target.value }))}
              className="input-field w-full text-xs"
            >
              <option value="chat">Чат</option>
              <option value="image">Изображения</option>
              <option value="video">Видео</option>
              <option value="music">Музыка</option>
            </select>
          </div>
          {/* cost_per_million_usd — только для платных */}
          {newModel.is_paid && (
            <>
              <div>
                <label className="block text-[10px] text-gray-600 mb-1">Стоимость у провайдера ($/шт)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newModel.cost_per_million_usd ?? ''}
                  onChange={(e) => setNewModel((p) => ({ ...p, cost_per_million_usd: e.target.value ? parseFloat(e.target.value) : null }))}
                  placeholder={newModel.category === 'image' ? '0.02 (за 1 изображение)' : '10.00 (за 1M токенов)'}
                  className="input-field w-full text-xs"
                />
              </div>
              {(newModel.category === 'image' || newModel.category === 'video') && (
                <div>
                  <label className="block text-[10px] text-gray-600 mb-1">
                    {newModel.category === 'image' ? 'Подкатегория' : 'Тип генерации'}
                  </label>
                  <select
                    value={newModel.category_sub || ''}
                    onChange={(e) => setNewModel((p) => ({ ...p, category_sub: e.target.value || null }))}
                    className="input-field w-full text-xs"
                  >
                    {newModel.category === 'image' ? (
                      <>
                        <option value="">Генератор (по умолчанию)</option>
                        <option value="edit">Редактор картинок</option>
                      </>
                    ) : (
                      <>
                        <option value="">Текст → Видео (по умолчанию)</option>
                        <option value="i2v">Фото → Видео</option>
                        <option value="v2v">Видео → Видео</option>
                      </>
                    )}
                  </select>
                </div>
              )}
              {(newModel.category === 'music' || newModel.category === 'image' || newModel.category === 'video') && (
                <div>
                  <label className="block text-[10px] text-gray-600 mb-1">API Endpoint</label>
                  <input
                    type="text"
                    value={newModel.api_endpoint ?? ''}
                    onChange={(e) => setNewModel((p) => ({ ...p, api_endpoint: e.target.value || null }))}
                    placeholder='lyria3/pro'
                    className="input-field w-full text-xs"
                  />
                </div>
              )}
            </>
          )}
        </div>

        <div>
          <label className="block text-[10px] text-gray-600 mb-1">Системный промпт</label>
          <textarea
            value={newModel.system_prompt ?? ''}
            onChange={(e) => setNewModel((p) => ({ ...p, system_prompt: e.target.value }))}
            rows={2}
            placeholder="Системный промпт..."
            className="input-field w-full resize-none text-xs"
          />
        </div>

        <div className="flex justify-end">
          <button
            onClick={() => createModel(isPaid)}
            disabled={creating || !newModel.model_name?.trim()}
            className="gradient-btn px-4 py-2 rounded-xl text-xs flex items-center gap-2 disabled:opacity-40"
          >
            {creating ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Plus className="w-3.5 h-3.5" />
            )}
            {creating ? 'Создание...' : 'Создать модель'}
          </button>
        </div>
      </div>
    )
  }

  const renderAddGroup = (type: 'free' | 'paid', category: string, label: string) => {
    const key = `${type}-${category}`
    const isOpen = showAddForm === key

    return (
      <>
        {isOpen && renderAddForm(type, category)}
        <button
          onClick={() => {
            setShowAddForm(isOpen ? null : key)
            setNewModel((p) => ({ ...p, is_paid: type === 'paid', category }))
          }}
          className="w-full p-3 rounded-xl border-2 border-dashed border-white/5 text-gray-500 hover:border-indigo-500/30 hover:text-indigo-400 text-sm transition-all flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Добавить {label} модель
        </button>
      </>
    )
  }

  const getModelsForSubTab = () => {
    switch (subTab) {
      case 'freeChat':
        return { models: chatFreeModels, type: 'free' as const, category: 'chat', addLabel: 'бесплатную текстовую' }
      case 'paidChat':
        return { models: chatPaidModels, type: 'paid' as const, category: 'chat', addLabel: 'платную текстовую' }
      case 'image':
        return { models: imageModels.filter(m => !m.category_sub || m.category_sub === 'generate'), type: 'paid' as const, category: 'image', addLabel: 'модель для картинок' }
      case 'imageEdit':
        return { models: imageModels.filter(m => m.category_sub === 'edit'), type: 'paid' as const, category: 'image', addLabel: 'модель для редактора' }
      case 'videoT2v':
        return { models: videoModels.filter(m => !m.category_sub || m.category_sub === 't2v'), type: 'paid' as const, category: 'video', addLabel: 'модель Текст→Видео' }
      case 'videoI2v':
        return { models: videoModels.filter(m => m.category_sub === 'i2v'), type: 'paid' as const, category: 'video', addLabel: 'модель Фото→Видео' }
      case 'videoV2v':
        return { models: videoModels.filter(m => m.category_sub === 'v2v'), type: 'paid' as const, category: 'video', addLabel: 'модель Видео→Видео' }
      case 'music':
        return { models: musicModels, type: 'paid' as const, category: 'music', addLabel: 'модель для музыки' }
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-indigo-400 animate-spin" />
      </div>
    )
  }

  const current = getModelsForSubTab()

  return (
    <div className="space-y-4">
      {/* Курс USD/RUB */}
      <div className="flex items-center gap-4 px-4 py-2 glass-card rounded-xl">
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-gray-500">Курс USD/RUB (ЦБ РФ):</span>
          <span className="text-sm font-semibold text-indigo-300">{usdRate.toFixed(2)}₽</span>
        </div>
        <div className="text-[10px] text-gray-600">
          Коэффициент 1:2 — пользователь платит вдвое себестоимости
        </div>
        <button
          onClick={async () => {
            const { data } = await client.get('/admin/ai-models/usd-rate?refresh=true').catch(() => ({ data: { rate: 80 } }))
            if (data?.rate) setUsdRate(data.rate)
          }}
          className="ml-auto px-3 py-1 rounded-lg bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20 text-[10px] transition-all"
        >
          Обновить курс
        </button>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 border-b border-white/5 pb-2 overflow-x-auto">
        {subTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setSubTab(tab.id)}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all whitespace-nowrap
              ${subTab === tab.id
                ? 'bg-indigo-500/10 text-indigo-300 border border-indigo-500/20'
                : 'text-gray-400 hover:text-gray-200 hover:bg-white/5'
              }
            `}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="grid grid-cols-2 gap-3">
        {current.models.map(renderModelCard)}
        {renderAddGroup(current.type, current.category, current.addLabel)}
      </div>
    </div>
  )
}


export default function Admin() {
  const [modelErrors, setModelErrors] = useState<any[]>([])
  const [errorsLoading, setErrorsLoading] = useState(true)
  const [showAllTop, setShowAllTop] = useState(false)
  const [showAllPurchases, setShowAllPurchases] = useState(false)
  const [purchasesDetail, setPurchasesDetail] = useState<any[]>([])
  const [purchasesLoading, setPurchasesLoading] = useState(false)

  const [stats, setStats] = useState<AdminStats>({
    totalUsers: 0,
    activeUsers: 0,
    newUsersToday: 0,
    newUsersWeek: 0,
    activeToday: 0,
    errorsToday: 0,
    topModels: [],
    purchasesToday: 0,
    purchasesWeek: 0,
    users: [],
  })
  const [isLoading, setIsLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'tokens' | 'settings' | 'apikeys' | 'ai-models' | 'landing' | 'database' | 'agents' | 'seo' | 'payments'>('overview')

  useEffect(() => {
    fetchStats()
    fetchModelErrors()
  }, [])

  const fetchModelErrors = async () => {
    try {
      setErrorsLoading(true)
      const { data } = await client.get('/admin/model-errors?limit=30')
      setModelErrors(data)
    } catch {
      // ignore
    } finally {
      setErrorsLoading(false)
    }
  }

  const fetchStats = async () => {
    try {
      const { data } = await client.get('/admin/stats')
      setStats(data)
    } catch {
      // ignore
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
      <h1 className="text-2xl font-bold gradient-text flex items-center gap-2">
        <Shield className="w-6 h-6" />
        Панель администратора
      </h1>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/5 pb-2">
        {[
          { id: 'overview', label: 'Обзор', icon: BarChart3 },
          { id: 'users', label: 'Пользователи', icon: Users },
          { id: 'apikeys', label: 'API-ключи', icon: Key },
          { id: 'tokens', label: 'Токены', icon: Gift },
          { id: 'settings', label: 'Настройки', icon: Settings },
          { id: 'ai-models', label: 'Модели', icon: Bot },
          { id: 'agents', label: 'Агенты', icon: Sparkles },
          { id: 'landing', label: 'Лендинг', icon: Layout },
          { id: 'database', label: 'БД', icon: Database },
          { id: 'seo', label: 'SEO', icon: Search },
          { id: 'payments', label: 'Платежи', icon: CreditCard },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`
              flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-xs transition-all
              ${activeTab === tab.id
                ? 'bg-indigo-500/10 text-indigo-300 border border-indigo-500/20'
                : 'text-gray-400 hover:text-gray-200 hover:bg-white/5'
              }
            `}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <>
          {/* Dashboard cards — 3x2 grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* 1. Пользователи */}
            <div className="glass-card p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-indigo-500/10">
                  <Users className="w-5 h-5 text-indigo-400" />
                </div>
                <span className="text-xs text-gray-500">Пользователи</span>
              </div>
              <p className="text-3xl font-bold text-white">{stats.totalUsers}</p>
              <p className="text-xs text-green-400 mt-1">{stats.activeUsers} активных</p>
            </div>

            {/* 2. Новых сегодня / неделя */}
            <div className="glass-card p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-emerald-500/10">
                  <Activity className="w-5 h-5 text-emerald-400" />
                </div>
                <span className="text-xs text-gray-500">Новых пользователей</span>
              </div>
              <p className="text-3xl font-bold text-emerald-300">{stats.newUsersToday}</p>
              <p className="text-xs text-gray-500 mt-1">{stats.newUsersWeek} за неделю</p>
            </div>

            {/* 3. Активных сегодня */}
            <div className="glass-card p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-cyan-500/10">
                  <Flame className="w-5 h-5 text-cyan-400" />
                </div>
                <span className="text-xs text-gray-500">Активных сегодня</span>
              </div>
              <p className="text-3xl font-bold text-cyan-300">{stats.activeToday}</p>
              <p className="text-xs text-gray-500 mt-1">пользователей совершали действия</p>
            </div>

            {/* 4. Ошибки API сегодня */}
            <div className="glass-card p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className={`p-2 rounded-lg ${stats.errorsToday > 0 ? 'bg-red-500/10' : 'bg-green-500/10'}`}>
                  <AlertCircle className={`w-5 h-5 ${stats.errorsToday > 0 ? 'text-red-400' : 'text-green-400'}`} />
                </div>
                <span className="text-xs text-gray-500">Ошибки API сегодня</span>
              </div>
              <p className={`text-3xl font-bold ${stats.errorsToday > 0 ? 'text-red-300' : 'text-green-300'}`}>{stats.errorsToday}</p>
              <p className={`text-xs mt-1 ${stats.errorsToday > 0 ? 'text-red-400' : 'text-green-400'}`}>
                {stats.errorsToday > 0 ? '⚠️ есть сбои провайдеров' : '✅ всё работает'}
              </p>
            </div>

            {/* 5. Пополнения */}
            <div className="glass-card p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-amber-500/10">
                  <Star className="w-5 h-5 text-amber-400" />
                </div>
                <span className="text-xs text-gray-500">Пополнений ⭐</span>
              </div>
              <p className="text-3xl font-bold text-amber-300">{stats.purchasesToday.toLocaleString()}</p>
              <p className="text-xs text-gray-500 mt-1">{stats.purchasesWeek.toLocaleString()} за неделю</p>
              {(stats.purchasesToday > 0 || stats.purchasesWeek > 0) && (
                <div className="mt-2">
                  {purchasesDetail.length > 0 && showAllPurchases && (
                    <div className="max-h-[200px] overflow-y-auto space-y-1 mb-2 border-t border-white/5 pt-2">
                      {purchasesDetail.map((p: any, i: number) => (
                        <div key={i} className="flex items-center gap-2 text-xs">
                          <span className="text-gray-400">{p.user_email}</span>
                          <span className="ml-auto text-amber-300 font-medium">+{p.amount.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    onClick={async () => {
                      if (purchasesDetail.length === 0) {
                        setPurchasesLoading(true)
                        try {
                          const { data } = await client.get('/admin/purchases')
                          setPurchasesDetail(data.today)
                        } catch {}
                        setPurchasesLoading(false)
                      }
                      setShowAllPurchases(!showAllPurchases)
                    }}
                    className="text-xs hover:text-white transition-colors"
                  >
                    {purchasesLoading ? (
                      <span className="text-gray-500">Загрузка...</span>
                    ) : showAllPurchases ? (
                      <span className="text-indigo-400">▲ Свернуть</span>
                    ) : (
                      <span className="text-indigo-400">▼ Показать все ({stats.purchasesToday.toLocaleString()} ⭐)</span>
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* 6. ТОП-3 популярных модели */}
            <div className="glass-card p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-purple-500/10">
                  <TrendingUp className="w-5 h-5 text-purple-400" />
                </div>
                <span className="text-xs text-gray-500">Популярные сегодня</span>
              </div>
              <div className="space-y-1">
                {stats.topModels.length === 0 ? (
                  <p className="text-sm text-gray-400">Нет генераций</p>
                ) : (
                  (showAllTop ? stats.topModels : stats.topModels.slice(0, 3)).map(([model, count]: [string, number], i: number) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <span className="text-gray-500 w-4">{i + 1}.</span>
                      <span className="text-gray-300 truncate flex-1">{model}</span>
                      <span className="text-purple-300 font-medium">{count}</span>
                    </div>
                  ))
                )}
                {stats.topModels.length > 3 && (
                  <button
                    onClick={() => setShowAllTop(!showAllTop)}
                    className="text-xs mt-1 hover:text-white transition-colors"
                  >
                    {showAllTop
                      ? '▲ Свернуть'
                      : <span className="text-indigo-400">▼ Показать все ({stats.topModels.length})</span>
                    }
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Quick actions */}
          <div className="glass-card p-6">
            <h3 className="text-sm font-semibold text-gray-200 mb-4">Быстрые действия</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <button
                onClick={() => setActiveTab('users')}
                className="flex items-center gap-3 p-3 rounded-xl glass-hover text-sm text-gray-300 hover:text-white transition-all"
              >
                <Users className="w-4 h-4 text-indigo-400" />
                Управление пользователями
              </button>
              <button
                onClick={() => setActiveTab('tokens')}
                className="flex items-center gap-3 p-3 rounded-xl glass-hover text-sm text-gray-300 hover:text-white transition-all"
              >
                <Coins className="w-4 h-4 text-amber-400" />
                Управление токенами
              </button>
              <button
                onClick={() => setActiveTab('settings')}
                className="flex items-center gap-3 p-3 rounded-xl glass-hover text-sm text-gray-300 hover:text-white transition-all"
              >
                <Settings className="w-4 h-4 text-purple-400" />
                Настройки моделей
              </button>
            </div>
          </div>

          {/* Model errors */}
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-200 flex items-center gap-2">
                {modelErrors.length > 0 ? (
                  <AlertCircle className="w-4 h-4 text-red-400" />
                ) : (
                  <CheckCircle className="w-4 h-4 text-green-400" />
                )}
                Ошибки платных моделей
              </h3>
              {modelErrors.length > 0 && (
                <span className="text-xs px-2 py-1 rounded-full bg-red-500/20 text-red-300">
                  {modelErrors.length} ошибок
                </span>
              )}
            </div>

            {errorsLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                Загрузка...
              </div>
            ) : modelErrors.length === 0 ? (
              <div className="flex items-center gap-2 text-sm text-green-400">
                <CheckCircle className="w-4 h-4" />
                Внешние AI-модели работают без ошибок
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/5 text-gray-500">
                      <th className="text-left py-2 pr-3 font-medium">Модель</th>
                      <th className="text-left py-2 pr-3 font-medium">Пользователь</th>
                      <th className="text-left py-2 pr-3 font-medium">Время</th>
                      <th className="text-left py-2 font-medium">Ошибка</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modelErrors.map((err: any, i: number) => (
                      <tr key={i} className="border-b border-white/5 text-gray-300 hover:bg-white/5">
                        <td className="py-2 pr-3">
                          <span className="inline-flex items-center gap-1">
                            <span className={
                              err.category === 'image' ? 'text-purple-400' :
                              err.category === 'video' ? 'text-blue-400' :
                              err.category === 'music' ? 'text-pink-400' :
                              'text-indigo-400'
                            }>
                              {err.category === 'image' ? '🖼' :
                               err.category === 'video' ? '🎬' :
                               err.category === 'music' ? '🎵' : '💬'}
                            </span>
                            <span className="font-mono text-xs">{err.model}</span>
                          </span>
                        </td>
                        <td className="py-2 pr-3 text-xs text-gray-400">{err.user_email}</td>
                        <td className="py-2 pr-3 text-xs text-gray-400 whitespace-nowrap">{err.time}</td>
                        <td className="py-2 text-xs text-red-300">{err.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'users' && (
        <UsersTabContent users={stats.users} onRefresh={fetchStats} />
      )}

      {activeTab === 'apikeys' && <ApiKeys />}

      {activeTab === 'tokens' && <TokensTab users={stats.users} fetchStats={fetchStats} />}

      {activeTab === 'ai-models' && <AiModelsTab />}

      {activeTab === 'agents' && <AgentsTab />}

      {activeTab === 'landing' && <LandingCms />}

      {activeTab === 'seo' && <SeoTab />}

      {activeTab === 'payments' && <PaymentsTab />}

      {activeTab === 'database' && <DatabaseTab />}

      {activeTab === 'settings' && (
        <div className="space-y-4">
          <div className="glass-card p-6">
            <h3 className="text-sm font-semibold text-gray-200 mb-4 flex items-center gap-2">
              <Settings className="w-4 h-4 text-indigo-400" />
              Настройки платформы
            </h3>

            <p className="text-xs text-gray-500">
              Настройки управления токенами и бонусами доступны ниже. Остальные настройки платформы пока управляются через бэкенд.
            </p>
          </div>

          <div className="glass-card p-6">
            <h3 className="text-sm font-semibold text-gray-200 mb-4 flex items-center gap-2">
              <Coins className="w-4 h-4 text-emerald-400" />
              Бесплатные токены
            </h3>
            <p className="text-xs text-gray-500 mb-4">
              Эти суммы автоматически применяются при регистрации, реферальных приглашениях и на главной / профиле.
            </p>
            <BonusConfigForm />
          </div>

          <div className="glass-card p-6">
            <h3 className="text-sm font-semibold text-gray-200 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              Опасная зона
            </h3>
            <p className="text-xs text-gray-500 mb-4">
              Действия в этой зоне необратимы
            </p>
            <button className="px-4 py-2 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm hover:bg-red-500/20 transition-all">
              Очистить базу данных
            </button>
          </div>
        </div>
      )}
    </div>
  )
}


function DatabaseTab() {
  const [backups, setBackups] = useState<any[]>([])
  const [currentSize, setCurrentSize] = useState(0)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const fetchList = () => {
    setLoading(true)
    client.get('/admin/backup/list')
      .then(({ data }) => {
        setBackups(data.backups)
        setCurrentSize(data.current_size)
      })
      .catch(() => setMsg({ type: 'error', text: 'Ошибка загрузки списка' }))
      .finally(() => setLoading(false))
  }

  useEffect(() => { fetchList() }, [])

  const handleSave = async () => {
    setSaving(true)
    setMsg(null)
    try {
      const { data } = await client.post('/admin/backup/save')
      setMsg({ type: 'success', text: data.message })
      fetchList()
    } catch {
      setMsg({ type: 'error', text: 'Ошибка создания бэкапа' })
    } finally {
      setSaving(false)
    }
  }

  const handleRestore = async (name: string) => {
    if (!confirm('Восстановить БД из ' + name + '? Сервер будет перезапущен.')) return
    setMsg(null)
    try {
      await client.post('/admin/backup/restore', { name })
      setMsg({ type: 'success', text: 'Восстановление выполнено. Обновите страницу.' })
      fetchList()
    } catch (err: any) {
      setMsg({ type: 'error', text: err?.response?.data?.detail || 'Ошибка восстановления' })
    }
  }

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024*1024) return (bytes/1024).toFixed(1) + ' KB'
    return (bytes/1024/1024).toFixed(1) + ' MB'
  }

  const formatDate = (iso: string) => {
    const d = new Date(iso)
    return d.toLocaleString('ru-RU', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  }

  return (
    <div className="space-y-4">
      {/* Текущая БД */}
      <div className="glass-card p-6">
        <h3 className="text-sm font-semibold text-gray-200 mb-4 flex items-center gap-2">
          <Database className="w-4 h-4 text-indigo-400" />
          Состояние базы данных
        </h3>
        <div className="text-xs text-gray-500 mb-4">
          Текущий размер: <span className="text-gray-300">{formatSize(currentSize)}</span>
          &nbsp;·&nbsp; Авто-бэкап: каждый час (хранятся 3 последних)
        </div>

        <div className="flex gap-3">
          <button onClick={handleSave} disabled={saving}
            className="gradient-btn px-4 py-2 rounded-xl text-sm flex items-center gap-2 disabled:opacity-40">
            <Save className="w-4 h-4" /> {saving ? 'Сохранение...' : 'Сохранить сейчас'}
          </button>

          <a href={`${client.defaults.baseURL}/admin/backup/download/latest?t=${Date.now()}`}
            className="gradient-btn px-4 py-2 rounded-xl text-sm flex items-center gap-2 inline-flex">
            <Download className="w-4 h-4" /> Скачать
          </a>
        </div>

        {msg && (
          <div className={`mt-3 text-xs px-3 py-2 rounded-xl ${msg.type === 'success' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
            {msg.text}
          </div>
        )}
      </div>

      {/* Список бэкапов */}
      <div className="glass-card p-6">
        <h3 className="text-sm font-semibold text-gray-200 mb-4 flex items-center gap-2">
          <RefreshCw className="w-4 h-4 text-emerald-400" />
          Сохранённые копии ({backups.length}/3)
        </h3>

        {loading ? (
          <div className="text-xs text-gray-500">Загрузка...</div>
        ) : backups.length === 0 ? (
          <div className="text-xs text-gray-500">Нет сохранённых бэкапов</div>
        ) : (
          <div className="space-y-2">
            {backups.map((b, i) => (
              <div key={b.name}
                className="flex items-center justify-between p-3 rounded-xl glass-hover">
                <div className="flex items-center gap-3">
                  <Database className="w-4 h-4 text-gray-500" />
                  <div>
                    <p className="text-xs text-gray-300">{b.name}</p>
                    <p className="text-[10px] text-gray-600">{formatDate(b.created)} · {formatSize(b.size)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <a href={`${client.defaults.baseURL}/admin/backup/download/${b.name}`}
                    download={b.name}
                    className="p-2 rounded-lg hover:bg-white/5 text-gray-500 hover:text-indigo-400 transition-all">
                    <Download className="w-3.5 h-3.5" />
                  </a>
                  <button onClick={() => handleRestore(b.name)}
                    className="p-2 rounded-lg hover:bg-white/5 text-gray-500 hover:text-amber-400 transition-all"
                    title="Восстановить">
                    <RotateCcw className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================
// AgentsTab — вкладка управления ИИ-агентами
// ============================================================
function AgentsTab() {
  const TOOL_DESCRIPTIONS: Record<string, string> = {
    search_web: 'Search the web for current information',
    fetch_url: 'Fetch and extract content from a URL',
    analyze_text: 'Analyze text: sentiment, keywords, summary',
    format_json: 'Format and validate JSON data',
  }
  const [agents, setAgents] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const AVAILABLE_TOOLS = ['search_web', 'fetch_url', 'analyze_text', 'format_json', 'parse_google_doc']
  const [newAgent, setNewAgent] = useState<any>({ name: '', emoji: '🤖', color: 'indigo', description: '', long_description: '', system_prompt: '', model: '', provider: '' })
  const [selectedToolNames, setSelectedToolNames] = useState<string[]>([])
  const [editId, setEditId] = useState<string | null>(null)
  const [editData, setEditData] = useState<any>(null)
  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const [showTools, setShowTools] = useState<string | null>(null)
  const [agentTools, setAgentTools] = useState<any[]>([])
  const [aiModels, setAiModels] = useState<any[]>([])

  // Загружаем список AI-моделей для выпадающего списка
  useEffect(() => {
    client.get('/admin/ai-models').then(res => {
      setAiModels(res.data || [])
    }).catch(() => {})
  }, [])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const fetchAgents = useCallback(async () => {
    try {
      setLoading(true)
      const { data } = await client.get('/agents')
      setAgents((data || []).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)))
    } catch (e) {
      toast.error('Ошибка загрузки агентов')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchAgents() }, [fetchAgents])

  const saveOrder = async (updated) => {
    setAgents(updated)
    for (let i = 0; i < updated.length; i++) {
      try {
        await client.put(`/agents/${updated[i].id}`, { sort_order: i })
      } catch (e) { /* ignore */ }
    }
    fetchAgents()
  }

  const handleDragEnd = (event) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIdx = agents.findIndex(a => a.id === active.id)
    const newIdx = agents.findIndex(a => a.id === over.id)
    if (oldIdx < 0 || newIdx < 0) return
    const updated = [...agents]
    const [moved] = updated.splice(oldIdx, 1)
    updated.splice(newIdx, 0, moved)
    saveOrder(updated)
  }

  const handleToggle = async (id, current) => {
    try {
      await client.put(`/agents/${id}`, { is_active: !current })
      setAgents(prev => prev.map(a => a.id === id ? { ...a, is_active: !current } : a))
      toast.success('Статус обновлён')
    } catch { toast.error('Ошибка') }
  }

  const handleEdit = (agent) => {
    setEditId(agent.id)
    setEditData({ ...agent, model: agent.model || '', provider: agent.provider || '' })
  }

  const handleSave = async () => {
    if (!editId || !editData) return
    try {
      await client.put(`/agents/${editId}`, editData)
      setAgents(prev => prev.map(a => a.id === editId ? { ...a, ...editData } : a))
      setEditId(null)
      setEditData(null)
      toast.success('Агент обновлён')
    } catch { toast.error('Ошибка сохранения') }
  }

  const handleDelete = async (id) => {
    if (!confirm('Удалить агента?')) return
    try {
      await client.delete(`/agents/${id}`)
      setAgents(prev => prev.filter(a => a.id !== id))
      toast.success('Агент удалён')
    } catch { toast.error('Ошибка удаления') }
  }

  const handleCreate = async () => {
    if (!newAgent.name.trim()) { toast.error('Введите имя агента'); return }
    try {
      const { data } = await client.post('/agents', { ...newAgent })
      // Добавляем выбранные инструменты
      for (const toolName of selectedToolNames) {
        await client.post(`/agents/${data.id}/tools`, {
          name: toolName,
          description: TOOL_DESCRIPTIONS[toolName] || toolName,
          handler: toolName,
          parameters: '{}',
          is_active: true,
        })
      }
      setAgents(prev => [...prev, { ...data, tools: selectedToolNames.map(n => ({ name: n, handler: n, is_active: true })) }])
      setShowCreate(false)
      setNewAgent({ name: '', emoji: '🤖', color: 'indigo', description: '', long_description: '', system_prompt: '', model: '', provider: '' })
      setSelectedToolNames([])
      toast.success('Агент создан')
    } catch { toast.error('Ошибка создания') }
  }

  const handleUploadAvatar = async (agentId) => {
    if (!avatarFile) return
    try {
      const fd = new FormData()
      fd.append('file', avatarFile)
      await client.post(`/agents/upload-avatar/${agentId}`, fd, {
        headers: { 'Content-Type': 'application/octet-stream' }
      })
      toast.success('Аватар загружен')
      setAvatarFile(null)
    } catch { toast.error('Ошибка загрузки') }
  }

  const fetchTools = async (agentId) => {
    try {
      const { data } = await client.get(`/agents/${agentId}/tools`)
      setAgentTools(data || [])
      setShowTools(agentId)
    } catch { toast.error('Ошибка загрузки инструментов') }
  }

  const toggleTool = async (agentId, toolId, current) => {
    try {
      await client.put(`/agents/tools/${toolId}`, { is_active: !current })
      setAgentTools(prev => prev.map(t => t.id === toolId ? { ...t, is_active: !current } : t))
      // Синхронизируем с общим списком agents
      setAgents(prev => prev.map(a => {
        if (a.id !== agentId) return a
        return {
          ...a,
          tools: (a.tools || []).map(t =>
            t.id === toolId ? { ...t, is_active: !current } : t
          )
        }
      }))
    } catch { toast.error('Ошибка') }
  }

  const editField = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }))
  }

  const colors = {
    indigo: 'from-indigo-500/20 to-indigo-500/5',
    emerald: 'from-emerald-500/20 to-emerald-500/5',
    blue: 'from-blue-500/20 to-blue-500/5',
    amber: 'from-amber-500/20 to-amber-500/5',
    red: 'from-red-500/20 to-red-500/5',
    purple: 'from-purple-500/20 to-purple-500/5',
    cyan: 'from-cyan-500/20 to-cyan-500/5',
    pink: 'from-pink-500/20 to-pink-500/5',
  }

  function SortableCard({ agent, index }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: agent.id })
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
      zIndex: isDragging ? 10 : 1,
    }

    return (
      <div ref={setNodeRef} style={style} className="glass-card p-4 space-y-3 relative">
        <div className="flex items-start gap-3">
          <div className="flex flex-col items-center gap-0.5 pt-1">
            <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-0.5 rounded hover:bg-white/5 transition-colors">
              <GripVertical className="w-3.5 h-3.5 text-gray-500" />
            </button>
            <span className="text-[9px] text-gray-600 font-mono">{index + 1}</span>
          </div>
          <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${colors[agent.color] || colors.indigo} flex items-center justify-center text-lg shrink-0 border border-white/10`}>
            {agent.emoji || '🤖'}
          </div>
          <div className="flex-1 min-w-0">
            {editId === agent.id ? (
              <input
                value={editData.name}
                onChange={e => editField('name', e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded px-2 py-1 text-sm text-white outline-none"
              />
            ) : (
              <h4 className="text-sm font-medium text-white inline-flex items-center gap-1.5">{agent.name}
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${
                      agent.provider === 'typegpt'
                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/20'
                        : 'bg-amber-500/20 text-amber-400 border border-amber-500/20'
                    }`}>
                      {agent.provider === 'typegpt' ? 'FREE' : '👑'}
                    </span>
                  </h4>
            )}
            <p className="text-[10px] text-gray-500 mt-0.5">
              {agent.skills?.length || 0} скиллов · {agent.tools?.length || 0} инструментов
            </p>
          </div>
          <button
            onClick={() => handleToggle(agent.id, agent.is_active)}
            className={`px-2 py-1 rounded-lg text-[10px] font-medium transition-all ${
              agent.is_active
                ? 'bg-green-500/20 text-green-400 border border-green-500/20'
                : 'bg-gray-500/20 text-gray-400 border border-gray-500/20'
            }`}
          >
            {agent.is_active ? 'Активен' : 'Выкл'}
          </button>
        </div>

        {editId === agent.id ? (
          <>
            <textarea
              value={editData.description}
              onChange={e => editField('description', e.target.value)}
              rows={2}
              placeholder="Описание"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-gray-300 outline-none resize-none"
            />
            <textarea
              value={editData.long_description || ''}
              onChange={e => editField('long_description', e.target.value)}
              rows={2}
              placeholder="Полное описание"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-gray-300 outline-none resize-none"
            />
            <textarea
              value={editData.system_prompt || ''}
              onChange={e => editField('system_prompt', e.target.value)}
              rows={4}
              placeholder="System prompt"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-gray-300 outline-none resize-none"
            />
            <div className="flex items-center gap-2 flex-wrap">
              <input
                value={editData.emoji || '🤖'}
                onChange={e => editField('emoji', e.target.value)}
                placeholder="🤖"
                className="w-12 text-center bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white outline-none"
              />
              <select
                value={editData.color || 'indigo'}
                onChange={e => editField('color', e.target.value)}
                className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-gray-300 outline-none"
              >
                {Object.keys(colors).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <select
                value={editData.model || ''}
                onChange={e => editField('model', e.target.value)}
                className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-gray-300 outline-none min-w-[160px]"
              >
                <option value="">— Выбрать модель —</option>
                {aiModels.filter(m => m.category === 'chat').map(m => (
                  <option key={m.id} value={m.model_name}>{m.model_name} {m.is_paid ? '👑' : 'FREE'}</option>
                ))}
              </select>
              <select
                value={editData.provider || 'openrouter'}
                onChange={e => editField('provider', e.target.value)}
                className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-gray-300 outline-none"
              >
                <option value="openrouter">OpenRouter</option>
                <option value="typegpt">TypeGPT</option>
              </select>
            </div>
          </>
        ) : (
          <p className="text-xs text-gray-400 line-clamp-2">{agent.description}</p>
        )}

        <div>
          <button
            onClick={() => showTools === agent.id ? setShowTools(null) : fetchTools(agent.id)}
            className="flex items-center gap-1.5 text-[10px] text-amber-400 hover:text-amber-300 transition-colors"
          >
            <Zap className="w-3 h-3" />
            Инструменты ({agent.tools?.length || 0})
          </button>
          {showTools === agent.id && (
            <div className="mt-2 space-y-1">
              {agentTools.length === 0 ? (
                <p className="text-[10px] text-gray-500">Нет инструментов</p>
              ) : (
                agentTools.map((t) => (
                  <div key={t.id} className="flex items-center justify-between px-2 py-1 rounded-lg bg-white/5">
                    <div className="flex items-center gap-1.5">
                      <Zap className={`w-3 h-3 ${t.is_active ? 'text-amber-400' : 'text-gray-600'}`} />
                      <span className={`text-[10px] ${t.is_active ? 'text-gray-300' : 'text-gray-600'}`}>{t.name}</span>
                    </div>
                    <button
                      onClick={() => toggleTool(agent.id, t.id, t.is_active)}
                      className={`px-2 py-0.5 rounded text-[9px] font-medium transition-all ${
                        t.is_active
                          ? 'bg-green-500/20 text-green-400 border border-green-500/20'
                          : 'bg-gray-500/10 text-gray-600 border border-gray-500/10'
                      }`}
                    >
                      {t.is_active ? 'Вкл' : 'Выкл'}
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-1">
          {(agent.skills || []).map((s, i) => (
            <div key={i} className="px-1.5 py-0.5 rounded bg-white/5 text-[9px] text-gray-500">{s.name}</div>
          ))}
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-white/5">
          <div className="flex items-center gap-1">
            <label className="p-1.5 rounded-lg hover:bg-white/5 cursor-pointer transition-colors">
              <Upload className="w-3 h-3 text-gray-500" />
              <input type="file" className="hidden" accept="image/*"
                onChange={e => { const f = e.target.files?.[0]; if (f) { setAvatarFile(f); setTimeout(() => handleUploadAvatar(agent.id), 100) } }}
              />
            </label>
          </div>
          <div className="flex items-center gap-1">
            {editId === agent.id ? (
              <>
                <button onClick={handleSave} className="px-2 py-1 rounded-lg bg-green-500/20 text-[10px] text-green-400 hover:bg-green-500/30 transition-all">Сохранить</button>
                <button onClick={() => { setEditId(null); setEditData(null) }} className="px-2 py-1 rounded-lg text-[10px] text-gray-400 hover:text-white transition-colors">Отмена</button>
              </>
            ) : (
              <>
                <button onClick={() => handleEdit(agent)} className="px-2 py-1 rounded-lg bg-indigo-500/20 text-[10px] text-indigo-300 hover:bg-indigo-500/30 transition-all">
                  Редактировать
                </button>
                <button onClick={() => handleDelete(agent.id)} className="p-1.5 rounded-lg hover:bg-white/5 text-gray-500 hover:text-red-400 transition-all">
                  <Trash2 className="w-3 h-3" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-200 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-indigo-400" />
          ИИ-агенты ({agents.length})
        </h3>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-indigo-500/20 hover:bg-indigo-500/30 text-xs text-indigo-300 transition-all"
        >
          <Plus className="w-3.5 h-3.5" />
          Создать агента
        </button>
      </div>

      {showCreate && (
        <div className="glass-card p-4 space-y-3">
          <input
            value={newAgent.name}
            onChange={e => setNewAgent(p => ({ ...p, name: e.target.value }))}
            placeholder="Имя агента"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 outline-none focus:border-indigo-500/50"
          />
          <textarea
            value={newAgent.description}
            onChange={e => setNewAgent(p => ({ ...p, description: e.target.value }))}
            placeholder="Описание"
            rows={2}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 outline-none focus:border-indigo-500/50 resize-none"
          />
          <textarea
            value={newAgent.long_description}
            onChange={e => setNewAgent(p => ({ ...p, long_description: e.target.value }))}
            placeholder="Полное описание"
            rows={2}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 outline-none focus:border-indigo-500/50 resize-none"
          />
          <textarea
            value={newAgent.system_prompt}
            onChange={e => setNewAgent(p => ({ ...p, system_prompt: e.target.value }))}
            placeholder="System prompt (инструкция для агента)"
            rows={4}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 outline-none focus:border-indigo-500/50 resize-none"
          />
          <div className="flex items-center gap-2 flex-wrap">
            <input
              value={newAgent.emoji}
              onChange={e => setNewAgent(p => ({ ...p, emoji: e.target.value }))}
              placeholder="🤖"
              className="w-12 text-center bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white outline-none focus:border-indigo-500/50"
            />
            <select
              value={newAgent.color}
              onChange={e => setNewAgent(p => ({ ...p, color: e.target.value }))}
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-gray-300 outline-none focus:border-indigo-500/50"
            >
              {Object.keys(colors).map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select
              value={newAgent.model}
              onChange={e => setNewAgent(p => ({ ...p, model: e.target.value }))}
              className="flex-1 min-w-[160px] bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-sm text-gray-300 outline-none focus:border-indigo-500/50"
            >
              <option value="">— Выбрать модель —</option>
              {aiModels.filter(m => m.category === 'chat').map(m => (
                <option key={m.id} value={m.model_name}>{m.model_name} {m.is_paid ? '👑' : 'FREE'}</option>
              ))}
            </select>
            <select
              value={newAgent.provider}
              onChange={e => setNewAgent(p => ({ ...p, provider: e.target.value }))}
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-gray-300 outline-none focus:border-indigo-500/50"
            >
              <option value="openrouter">OpenRouter</option>
              <option value="typegpt">TypeGPT</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] text-gray-500 mb-1.5">Инструменты</label>
            <div className="flex flex-wrap gap-2">
              {AVAILABLE_TOOLS.map(toolName => (
                <label
                  key={toolName}
                  className={`flex items-center gap-1.5 px-2 py-1 rounded-lg border text-[10px] cursor-pointer transition-all ${
                    selectedToolNames.includes(toolName)
                      ? 'border-indigo-500/40 bg-indigo-500/10 text-indigo-300'
                      : 'border-white/5 bg-white/5 text-gray-400 hover:bg-white/10'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedToolNames.includes(toolName)}
                    onChange={() => setSelectedToolNames(prev =>
                      prev.includes(toolName)
                        ? prev.filter(n => n !== toolName)
                        : [...prev, toolName]
                    )}
                    className="sr-only"
                  />
                  <Zap className={`w-2.5 h-2.5 ${selectedToolNames.includes(toolName) ? 'text-amber-400' : 'text-gray-600'}`} />
                  {toolName}
                </label>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowCreate(false)} className="px-3 py-1.5 rounded-lg text-xs text-gray-400 hover:text-white transition-colors">Отмена</button>
            <button onClick={handleCreate} className="px-3 py-1.5 rounded-lg bg-indigo-500/20 hover:bg-indigo-500/30 text-xs text-indigo-300 transition-all">Создать</button>
          </div>
        </div>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={agents.map(a => a.id)} strategy={verticalListSortingStrategy}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {agents.map((agent, idx) => (
              <SortableCard key={agent.id} agent={agent} index={idx} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  )
}

// ============================================================
// UsersTabContent — управление пользователями
// ============================================================
function UsersTabContent({ users: _initialUsers, onRefresh }: { users: any[]; onRefresh: () => void }) {
  const [rows, setRows] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [perPage] = useState(50)
  const [totalPages, setTotalPages] = useState(1)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [sortBy, setSortBy] = useState('created_at')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [loading, setLoading] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [changingRole, setChangingRole] = useState<string | null>(null)
  const [changingBan, setChangingBan] = useState<string | null>(null)

  const fetchUsers = useCallback(async (p: number, q: string, sb: string, sd: 'asc' | 'desc') => {
    setLoading(true)
    try {
      const params = new URLSearchParams({ page: String(p), per_page: String(perPage) })
      if (q) params.set('search', q)
      if (sb) params.set('sort_by', sb)
      params.set('sort_dir', sd)
      const { data } = await client.get(`/admin/users?${params.toString()}`)
      setRows(data.users || [])
      setTotal(data.total || 0)
      setTotalPages(data.total_pages || 1)
      setPage(data.page || 1)
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Ошибка загрузки пользователей')
    } finally {
      setLoading(false)
    }
  }, [perPage])

  useEffect(() => {
    fetchUsers(page, search, sortBy, sortDir)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, sortBy, sortDir])

  const handleSort = (field: string) => {
    if (sortBy === field) {
      const next = sortDir === 'desc' ? 'asc' : 'desc'
      setSortDir(next)
      fetchUsers(1, search, field, next)
    } else {
      setSortBy(field)
      setSortDir('desc')
      fetchUsers(1, search, field, 'desc')
    }
  }

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setSearch(searchInput.trim())
    fetchUsers(1, searchInput.trim(), sortBy, sortDir)
  }

  const ClearSearch = () => {
    setSearchInput('')
    setSearch('')
    fetchUsers(1, '', sortBy, sortDir)
  }

  const handleDelete = async (userId: string, userName: string) => {
    if (!window.confirm(`Удалить пользователя ${userName}? Это действие необратимо.`)) return
    setDeletingId(userId)
    try {
      await client.delete(`/admin/users/${userId}`)
      toast.success(`Пользователь ${userName} удалён`)
      onRefresh()
      fetchUsers(page, search, sortBy, sortDir)
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Ошибка удаления')
    } finally {
      setDeletingId(null)
    }
  }

  const handleToggleRole = async (userId: string, userName: string, makeAdmin: boolean) => {
    setChangingRole(userId)
    try {
      await client.put(`/admin/users/${userId}/role`, { is_admin: makeAdmin })
      toast.success(`${userName} теперь ${makeAdmin ? 'Администратор' : 'Пользователь'}`)
      onRefresh()
      fetchUsers(page, search, sortBy, sortDir)
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Ошибка смены роли')
    } finally {
      setChangingRole(null)
    }
  }

  const handleToggleBan = async (userId: string, userName: string, ban: boolean) => {
    setChangingBan(userId)
    try {
      await client.put(`/admin/users/${userId}/ban`, { is_banned: ban })
      toast.success(`${userName} ${ban ? 'забанен' : 'разбанен'}`)
      onRefresh()
      fetchUsers(page, search, sortBy, sortDir)
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Ошибка')
    } finally {
      setChangingBan(null)
    }
  }

  const SortTh = ({ field, label, className = 'text-left py-3 px-2' }: { field: string; label: string; className?: string }) => (
    <th
      className={`${className} cursor-pointer select-none hover:text-indigo-300 transition-colors`}
      onClick={() => handleSort(field)}
      title="Сортировать"
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <span className="text-[9px] text-indigo-400">
          {sortBy === field ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </span>
    </th>
  )

  return (
    <div className="glass-card p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <h3 className="text-sm font-semibold text-gray-200">
          Список пользователей
          <span className="ml-2 text-[10px] font-normal text-gray-500">Всего: {total}</span>
        </h3>
        <form onSubmit={submitSearch} className="flex items-center gap-2 w-full md:w-72">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Поиск по имени или email..."
            className="flex-1 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-gray-300 outline-none focus:border-indigo-500/50"
          />
          {searchInput && (
            <button
              type="button"
              onClick={ClearSearch}
              className="px-2 py-1.5 rounded-lg text-[10px] text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
              title="Сбросить поиск"
            >
              ✕
            </button>
          )}
          <button
            type="submit"
            className="px-3 py-1.5 rounded-lg bg-indigo-500/20 hover:bg-indigo-500/30 text-xs text-indigo-300 transition-all"
          >
            Найти
          </button>
        </form>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-500 border-b border-white/5">
              <th className="text-left py-3 px-2">ID</th>
              <SortTh field="name" label="Имя" />
              <SortTh field="email" label="Email" />
              <SortTh field="created_at" label="Дата" />
              <SortTh field="plan" label="План" />
              <SortTh field="role" label="Роль" />
              <th className="text-left py-3 px-2">Чат</th>
              <th className="text-left py-3 px-2">Токены</th>
              <th className="text-right py-3 px-2">Действия</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={9} className="py-6 text-center text-xs text-gray-500">Загрузка...</td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={9} className="py-6 text-center text-xs text-gray-500">Пользователи не найдены</td>
              </tr>
            ) : (
              rows.map((u: any) => (
                <tr key={u.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="py-3 px-2 text-gray-400">#{u.id}</td>
                  <td className="py-3 px-2 text-gray-200">{u.name}</td>
                  <td className="py-3 px-2 text-gray-400">{u.email}</td>
                  <td className="py-3 px-2 text-gray-500 text-xs">
                    {u.createdAt ? new Date(u.createdAt).toLocaleDateString('ru-RU') : '-'}
                  </td>
                  <td className="py-3 px-2">
                    <span className={
                      u.plan === 'free' || !u.plan
                        ? 'text-green-400 bg-green-500/10 px-1.5 py-0.5 rounded text-[10px]'
                        : 'text-indigo-400 bg-indigo-500/10 px-1.5 py-0.5 rounded text-[10px]'
                    }>
                      {u.plan === 'free' || !u.plan ? 'Бесплатный' : u.plan}
                    </span>
                  </td>
                  <td className="py-3 px-2">
                    {u.isAdmin ? (
                      <span className="px-2 py-0.5 rounded text-[10px] bg-red-500/10 text-red-400">Админ</span>
                    ) : (
                      <span className="px-2 py-0.5 rounded text-[10px] bg-gray-500/10 text-gray-400">Юзер</span>
                    )}
                  </td>
                  <td className="py-3 px-2 text-gray-500 text-xs">{u.sessions ?? 0}</td>
                  <td className="py-3 px-2">
                    <span className="text-amber-300">{u.tokens?.toLocaleString() || 0}</span>
                  </td>
                  <td className="py-3 px-2 text-right whitespace-nowrap">
                    <button
                      onClick={() => handleToggleBan(u.id, u.name || u.email, !u.isBanned)}
                      disabled={changingBan === u.id}
                      className={`px-2 py-0.5 rounded text-[10px] border outline-none cursor-pointer transition-all ${
                        u.isBanned
                          ? 'bg-red-500/10 text-red-400 border-red-500/20'
                          : 'bg-green-500/10 text-green-400 border-green-500/20'
                      }`}
                      title={u.isBanned ? 'Разбанить' : 'Забанить'}
                    >
                      {u.isBanned ? '🚫 Бан' : '✅ OK'}
                    </button>
                    <select
                      value={u.isAdmin ? 'admin' : 'user'}
                      onChange={(e) => handleToggleRole(u.id, u.name || u.email, e.target.value === 'admin')}
                      disabled={changingRole === u.id}
                      className={`ml-1 px-2 py-0.5 rounded text-[10px] border outline-none cursor-pointer transition-all ${
                        u.isAdmin
                          ? 'bg-red-500/10 text-red-400 border-red-500/20'
                          : 'bg-gray-500/10 text-gray-400 border-gray-500/20'
                      }`}
                    >
                      <option value="user" className="bg-gray-900 text-gray-300">Юзер</option>
                      <option value="admin" className="bg-gray-900 text-red-300">Админ</option>
                    </select>
                    <button
                      onClick={() => handleDelete(u.id, u.name || u.email)}
                      disabled={deletingId === u.id}
                      className="ml-1 p-1.5 rounded-lg text-gray-600 hover:text-red-400 hover:bg-red-500/10 transition-all disabled:opacity-30 align-middle"
                      title="Удалить пользователя"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/5">
          <span className="text-[10px] text-gray-500">
            Страница {page} из {totalPages} · {perPage} на стр.
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => fetchUsers(page - 1, search, sortBy, sortDir)}
              disabled={page <= 1 || loading}
              className="px-3 py-1 rounded-lg bg-white/5 text-xs text-gray-300 hover:bg-white/10 disabled:opacity-30 transition-all"
            >
              ← Назад
            </button>
            <button
              onClick={() => fetchUsers(page + 1, search, sortBy, sortDir)}
              disabled={page >= totalPages || loading}
              className="px-3 py-1 rounded-lg bg-white/5 text-xs text-gray-300 hover:bg-white/10 disabled:opacity-30 transition-all"
            >
              Далее →
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// SeoTab — управление SEO-настройками
// ============================================================
const PAGE_META_PAGES = [
  { path: '/', label: 'Главная', icon: '🏠' },
  { path: '/login', label: 'Вход', icon: '🔑' },
  { path: '/register', label: 'Регистрация', icon: '📝' },
  { path: '/chat', label: 'Чат', icon: '💬' },
  { path: '/image', label: 'Изображения', icon: '🎨' },
  { path: '/video', label: 'Видео', icon: '🎬' },
  { path: '/music', label: 'Музыка', icon: '🎵' },
  { path: '/sheets', label: 'Таблицы', icon: '📊' },
  { path: '/translator', label: 'Переводчик', icon: '🌐' },
  { path: '/characters', label: 'Персонажи', icon: '👥' },
  { path: '/code', label: 'Код', icon: '💻' },
  { path: '/agents', label: 'ИИ-агенты', icon: '🤖' },
  { path: '/profile', label: 'Профиль', icon: '👤' },
  { path: '/payment', label: 'Тарифы', icon: '💳' },
  { path: '/partner', label: 'Партнёрская программа', icon: '🤝' },
  { path: '/pricing', label: 'Тарифы (публичные)', icon: '💰' },
  { path: '/terms', label: 'Пользовательское соглашение', icon: '📜' },
  { path: '/privacy', label: 'Политика конфиденциальности', icon: '🔒' },
  { path: '/support', label: 'Поддержка', icon: '📞' },
  { path: '/forgot-password', label: 'Сброс пароля', icon: '🔐' },
  { path: '/ai-models', label: 'AI-модели (публичные)', icon: '🤖' },
  { path: '/faq', label: 'FAQ', icon: '❓' },
  { path: '/verify-email', label: 'Подтверждение email', icon: '📧' },
]

function SeoTab() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [form, setForm] = useState({
    robots_txt: '',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    h1_text: '',
    og_image_url: '',
    site_url: 'https://chat-neuro.ru',
    head_code: '',
    llms_txt: '',
  })
  const [pageMeta, setPageMeta] = useState<Record<string, { title: string; description: string; h1: string }>>({})

  const fetchSettings = async () => {
    setLoading(true)
    try {
      const { data } = await client.get('/seo/settings')
      setForm({
        robots_txt: data.robots_txt || '',
        meta_title: data.meta_title || '',
        meta_description: data.meta_description || '',
        meta_keywords: data.meta_keywords || '',
        h1_text: data.h1_text || '',
        og_image_url: data.og_image_url || '',
        site_url: data.site_url || 'https://chat-neuro.ru',
        head_code: data.head_code || '',
        llms_txt: data.llms_txt || '',
      })
      // Parse page_meta JSON
      if (data.page_meta) {
        try {
          setPageMeta(JSON.parse(data.page_meta))
        } catch {}
      }
    } catch {
      setMsg({ type: 'error', text: '❌ Ошибка загрузки SEO-настроек' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchSettings() }, [])

  const handleSave = async (pagePath?: string) => {
    setSaving(true)
    setMsg(null)
    try {
      const payload: any = { ...form }
      // Only send page_meta if we have data
      const currentPageMeta = { ...pageMeta }
      // Clean empty entries
      for (const [k, v] of Object.entries(currentPageMeta)) {
        if (!v.title && !v.description && !v.h1) {
          delete currentPageMeta[k]
        }
      }
      payload.page_meta = Object.keys(currentPageMeta).length > 0 ? JSON.stringify(currentPageMeta) : '{}'
      await client.put('/seo/settings', payload)
      setMsg({ type: 'success', text: '✅ SEO-настройки сохранены!' })
    } catch {
      setMsg({ type: 'error', text: '❌ Ошибка сохранения' })
    } finally {
      setSaving(false)
    }
  }

  const handleSaveAll = () => handleSave()

  const updateField = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  const updatePageMeta = (path: string, field: string, value: string) => {
    setPageMeta(prev => ({
      ...prev,
      [path]: {
        title: prev[path]?.title || '',
        description: prev[path]?.description || '',
        h1: prev[path]?.h1 || '',
        [field]: value,
      }
    }))
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-indigo-400 animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Мета-теги для каждой страницы */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-200 flex items-center gap-2">
            <Globe className="w-4 h-4 text-indigo-400" />
            Мета-теги для страниц
          </h3>
          <button onClick={handleSaveAll} disabled={saving} className="px-4 py-2 rounded-lg bg-indigo-500/20 hover:bg-indigo-500/30 text-xs text-indigo-300 transition-all disabled:opacity-40 flex items-center gap-1.5">
            {saving ? <><Loader2 className="w-3 h-3 animate-spin" /> Сохранение...</> : <><Save className="w-3 h-3" /> Сохранить все</>}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {PAGE_META_PAGES.map(page => {
            const meta = pageMeta[page.path] || { title: '', description: '', h1: '' }
            return (
              <div key={page.path} className="glass-card p-4 space-y-3 border border-white/5 hover:border-indigo-500/20 transition-all">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">{page.icon}</span>
                  <div>
                    <p className="text-sm font-medium text-gray-200">{page.label}</p>
                    <p className="text-[10px] text-gray-500 font-mono">{page.path}</p>
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1">Meta Title</label>
                  <input
                    type="text"
                    value={meta.title}
                    onChange={e => updatePageMeta(page.path, 'title', e.target.value)}
                    placeholder="Заголовок страницы"
                    maxLength={256}
                    className="w-full px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-white outline-none focus:border-indigo-500/50"
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1">Meta Description</label>
                  <textarea
                    value={meta.description}
                    onChange={e => updatePageMeta(page.path, 'description', e.target.value)}
                    placeholder="Описание страницы"
                    rows={2}
                    maxLength={512}
                    className="w-full px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-white outline-none focus:border-indigo-500/50 resize-none"
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1">H1</label>
                  <input
                    type="text"
                    value={meta.h1}
                    onChange={e => updatePageMeta(page.path, 'h1', e.target.value)}
                    placeholder="Заголовок H1"
                    maxLength={256}
                    className="w-full px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-white outline-none focus:border-indigo-500/50"
                  />
                </div>
              </div>
            )
          })}
        </div>

        <div className="flex items-center gap-3 mt-6">
          <button onClick={handleSaveAll} disabled={saving} className="px-4 py-2 rounded-lg bg-indigo-500/20 hover:bg-indigo-500/30 text-xs text-indigo-300 transition-all disabled:opacity-40 flex items-center gap-1.5">
            {saving ? <><Loader2 className="w-3 h-3 animate-spin" /> Сохранение...</> : <><Save className="w-3 h-3" /> Сохранить все страницы</>}
          </button>
        </div>
      </div>

      {/* robots.txt */}
      <div className="glass-card p-6">
        <h3 className="text-sm font-semibold text-gray-200 mb-4 flex items-center gap-2">
          <Globe className="w-4 h-4 text-emerald-400" />
          robots.txt
        </h3>
        <textarea value={form.robots_txt} onChange={e => updateField('robots_txt', e.target.value)} rows={12} className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white font-mono outline-none focus:border-indigo-500/50 resize-none" />
        <div className="flex items-center gap-3 mt-4">
          <button onClick={handleSaveAll} disabled={saving} className="px-4 py-2 rounded-lg bg-indigo-500/20 hover:bg-indigo-500/30 text-xs text-indigo-300 transition-all disabled:opacity-40 flex items-center gap-1.5">
            {saving ? <><Loader2 className="w-3 h-3 animate-spin" /> Сохранение...</> : <><Save className="w-3 h-3" /> Сохранить robots.txt</>}
          </button>
        </div>
      </div>

      {/* Sitemap */}
      <div className="glass-card p-6">
        <h3 className="text-sm font-semibold text-gray-200 mb-4 flex items-center gap-2">
          <Globe className="w-4 h-4 text-sky-400" />
          Карта сайта (Sitemap)
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-[10px] text-gray-500 mb-1.5">Домен сайта</label>
            <input
              type="text"
              value={form.site_url}
              onChange={e => updateField('site_url', e.target.value)}
              placeholder="https://chat-neuro.ru"
              className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none focus:border-indigo-500/50"
            />
            <p className="text-[10px] text-gray-600 mt-1">
              Этот домен будет использоваться в sitemap.xml, sitemap_index.xml и robots.txt.
              При смене домена просто измените его здесь — все файлы сгенерируются с новым доменом.
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span>📄</span>
            <a href="/sitemap.xml" target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:text-indigo-300 underline">sitemap.xml</a>
            <span className="text-gray-600">|</span>
            <span>📄</span>
            <a href="/sitemap_index.xml" target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:text-indigo-300 underline">sitemap_index.xml</a>
            <span className="text-gray-600">|</span>
            <span>🤖</span>
            <a href="/robots.txt" target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:text-indigo-300 underline">robots.txt</a>
          </div>
        </div>
        <div className="flex items-center gap-3 mt-4">
          <button onClick={handleSaveAll} disabled={saving} className="px-4 py-2 rounded-lg bg-indigo-500/20 hover:bg-indigo-500/30 text-xs text-indigo-300 transition-all disabled:opacity-40 flex items-center gap-1.5">
            {saving ? <><Loader2 className="w-3 h-3 animate-spin" /> Сохранение...</> : <><Save className="w-3 h-3" /> Сохранить</>}
          </button>
        </div>
      </div>

      {/* llms.txt — инструкции для AI-агентов */}
      <div className="glass-card p-6">
        <h3 className="text-sm font-semibold text-gray-200 mb-4 flex items-center gap-2">
          <Bot className="w-4 h-4 text-emerald-400" />
          llms.txt — инструкции для AI-агентов
        </h3>
        <p className="text-[10px] text-gray-500 mb-3">
          Содержимое файла <code className="text-indigo-400">/llms.txt</code> — инструкции для LLM-агентов (ChatGPT, Claude, Gemini и др.).
          Подробнее: <a href="https://llmstxt.org/" target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:text-indigo-300 underline">llmstxt.org</a>
        </p>
        <textarea
          value={form.llms_txt}
          onChange={e => updateField('llms_txt', e.target.value)}
          rows={24}
          className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white font-mono outline-none focus:border-indigo-500/50 resize-y"
        />
        <div className="flex items-center gap-3 mt-4">
          <button onClick={handleSaveAll} disabled={saving} className="px-4 py-2 rounded-lg bg-indigo-500/20 hover:bg-indigo-500/30 text-xs text-indigo-300 transition-all disabled:opacity-40 flex items-center gap-1.5">
            {saving ? <><Loader2 className="w-3 h-3 animate-spin" /> Сохранение...</> : <><Save className="w-3 h-3" /> Сохранить llms.txt</>}
          </button>
          <a href="/llms.txt" target="_blank" rel="noopener noreferrer" className="text-xs text-gray-400 hover:text-gray-300 underline">Посмотреть /llms.txt</a>
        </div>
      </div>

      {/* Head код (счётчики, аналитика) */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-200 flex items-center gap-2">
            <Code className="w-4 h-4 text-indigo-400" />
            Head-код (счётчики, метрика, аналитика)
          </h3>
        </div>
        <p className="text-[10px] text-gray-500 mb-3">
          Код будет вставлен внутрь тега <code className="text-indigo-400">&lt;head&gt;</code> на всех страницах. 
          Используется для подключения Яндекс.Метрики, Google Analytics, пикселей соцсетей и других внешних скриптов.
        </p>
        <textarea
          value={form.head_code}
          onChange={e => updateField('head_code', e.target.value)}
          rows={8}
          className="w-full bg-black/40 border border-white/10 rounded-lg p-3 text-xs text-gray-300 font-mono focus:outline-none focus:border-indigo-500/50 transition-colors resize-y"
          placeholder={'<!-- Пример: -->\n<script>\n  (function(m,e,t,r,i,k,a){...})\n</script>'}
        />
      </div>

      {/* SEO-справка */}
      <div className="glass-card p-6">
        <h3 className="text-sm font-semibold text-gray-200 mb-4 flex items-center gap-2">
          <Shield className="w-4 h-4 text-amber-400" />
          SEO-справка
        </h3>
        <div className="text-[11px] text-gray-500 space-y-2 leading-relaxed">
          <p>🔹 <strong className="text-gray-300">Meta Title</strong> — заголовок в результатах поиска. Рекомендуется до 60 символов.</p>
          <p>🔹 <strong className="text-gray-300">Meta Description</strong> — описание под заголовком. Рекомендуется до 160 символов.</p>
          <p>🔹 <strong className="text-gray-300">H1</strong> — главный заголовок на странице.</p>
          <p>🔹 <strong className="text-gray-300">robots.txt</strong> — инструкции для поисковых роботов. Доступен на /robots.txt</p>
          <p>🔹 <strong className="text-gray-300">sitemap.xml</strong> — генерируется динамически с вашим доменом. Доступен на /sitemap.xml</p>
          <p>🔹 <strong className="text-gray-300">Домен</strong> — пропишите домен в блоке "Карта сайта" и sitemap/robots сгенерируются с новым доменом.</p>
          <p>🔹 <strong className="text-gray-300">llms.txt</strong> — инструкции для LLM-агентов (ChatGPT, Claude, Gemini). Доступен на /llms.txt. <a href="https://llmstxt.org/" target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:text-indigo-300 underline">Стандарт llmstxt.org</a></p>
          <p>🔹 <strong className="text-gray-300">JSON-LD</strong> — schema.org разметка встраивается на все страницы.</p>
        </div>
      </div>
    </div>
  )
}

// ============================================================
// PaymentsTab — управление заказами (платежами)
// ============================================================

function PaymentsTab() {
  const [orders, setOrders] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [confirmingId, setConfirmingId] = useState<string | null>(null)
  const [cancellingId, setCancellingId] = useState<string | null>(null)

  const fetchOrders = async () => {
    setLoading(true)
    try {
      const params = statusFilter ? `?status_filter=${statusFilter}` : ''
      const { data } = await client.get(`/payment/admin/orders${params}`)
      setOrders(data.orders || [])
    } catch {
      // ignore
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchOrders()
  }, [statusFilter])

  const handleConfirm = async (orderId: string) => {
    setConfirmingId(orderId)
    try {
      await client.post('/payment/admin/confirm', { order_id: orderId })
      toast.success('Заказ подтверждён, токены начислены')
      fetchOrders()
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Ошибка подтверждения')
    } finally {
      setConfirmingId(null)
    }
  }

  const handleCancel = async (orderId: string) => {
    setCancellingId(orderId)
    try {
      await client.post('/payment/admin/cancel', { order_id: orderId })
      toast.success('Заказ отменён')
      fetchOrders()
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Ошибка отмены')
    } finally {
      setCancellingId(null)
    }
  }

  const statusColors: Record<string, string> = {
    pending: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
    waiting_for_capture: 'text-sky-400 bg-sky-500/10 border-sky-500/20',
    succeeded: 'text-green-400 bg-green-500/10 border-green-500/20',
    cancelled: 'text-red-400 bg-red-500/10 border-red-500/20',
  }
  const statusLabels: Record<string, string> = {
    pending: 'Ожидает',
    waiting_for_capture: 'В обработке',
    succeeded: 'Подтверждён',
    cancelled: 'Отменён',
  }

  return (
    <div className="glass-card p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-white flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-sky-400" />
          Заказы и платежи
        </h2>
      </div>

      {/* Фильтр по статусу */}
      <div className="flex gap-2">
        {['', 'pending', 'waiting_for_capture', 'succeeded', 'cancelled'].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-xs transition-all ${
              statusFilter === s
                ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'
                : 'text-gray-500 hover:text-gray-300 border border-white/5'
            }`}
          >
            {s ? statusLabels[s] || s : 'Все'}
          </button>
        ))}
        <button
          onClick={fetchOrders}
          className="ml-auto px-3 py-1.5 rounded-lg text-xs text-gray-500 hover:text-gray-300 border border-white/5 hover:border-indigo-500/30 transition-all flex items-center gap-1"
        >
          <RefreshCw className="w-3 h-3" />
          Обновить
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : orders.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-8">Нет заказов</p>
      ) : (
        <div className="space-y-2">
          {orders.map((order) => (
            <div
              key={order.id}
              className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/5 hover:border-white/10 transition-all"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-mono text-gray-500">#{order.id.slice(0, 8)}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${statusColors[order.status] || 'text-gray-400'}`}>
                    {statusLabels[order.status] || order.status}
                  </span>
                </div>
                <div className="text-sm text-gray-300">
                  {order.user_email && <span className="text-indigo-400">{order.user_email}</span>}
                  <span className="mx-2">·</span>
                  <span className="text-amber-300">{order.plan_id}</span>
                  <span className="mx-2">·</span>
                  <span>{order.amount_rub}₽</span>
                  <span className="mx-2">→</span>
                  <span className="text-green-300">{order.tokens.toLocaleString()} CN</span>
                </div>
                <div className="text-[10px] text-gray-600 mt-0.5">
                  {new Date(order.created_at).toLocaleString('ru-RU')}
                  {order.confirmed_at && ` · подтверждён: ${new Date(order.confirmed_at).toLocaleString('ru-RU')}`}
                </div>
              </div>

              <div className="flex items-center gap-2 ml-4">
                {(order.status === 'pending' || order.status === 'waiting_for_capture') && (
                  <>
                    <button
                      onClick={() => handleConfirm(order.id)}
                      disabled={confirmingId === order.id}
                      className="px-3 py-1.5 rounded-lg text-xs bg-green-500/10 text-green-300 border border-green-500/20 hover:bg-green-500/20 transition-all disabled:opacity-40"
                    >
                      {confirmingId === order.id ? '...' : 'Подтвердить'}
                    </button>
                    <button
                      onClick={() => handleCancel(order.id)}
                      disabled={cancellingId === order.id}
                      className="px-3 py-1.5 rounded-lg text-xs bg-red-500/10 text-red-300 border border-red-500/20 hover:bg-red-500/20 transition-all disabled:opacity-40"
                    >
                      {cancellingId === order.id ? '...' : 'Отменить'}
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}